import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  BookOpen, MessageSquare, Heart, Users, Gamepad2, 
  FileText, Shield, ChevronRight, ChevronLeft, X, 
  Mic, Calendar, Award, Phone, Pause, Play
} from 'lucide-react';
import { authFetch } from '../lib/authFetch';

const AXIOM_LOGO = 'https://customer-assets.emergentagent.com/job_sponsor-bot-clinical/artifacts/kgiafx8p_AxiomLogo.png';

// Tour auto-play: 60 seconds total across 12 slides = 5000ms per slide.
const AUTOPLAY_SLIDE_MS = 5000;

const slides = [
  {
    id: 'welcome',
    icon: null,
    useLogo: true,
    title: 'Welcome to Axiom',
    subtitle: 'Your 24/7 Recovery Partner',
    description: 'This is your personal Recovery programme. Everything you need: daily check-ins, an AI sponsor, readings, meetings and games: all in one place. Let me show you around.',
    color: 'from-blue-700 to-blue-900',
    accent: 'bg-blue-500',
  },
  {
    id: 'dashboard',
    icon: Calendar,
    title: 'Your Dashboard',
    subtitle: 'Home Base',
    description: 'Your dashboard shows your sobriety clock, daily tasks (Journal and Mental Health check-in) and weekly tasks (Meetings, Readings and Self-Work). Complete them to progress through the 32-week programme that starts with the 2-Week Window.',
    color: 'from-indigo-700 to-indigo-900',
    accent: 'bg-indigo-500',
  },
  {
    id: 'tudor',
    icon: MessageSquare,
    title: 'Chat with Tudor',
    subtitle: 'Your AI Sponsor',
    description: 'Tudor is your around-the-clock Recovery sponsor. Talk or type anytime: about cravings, life stress or just to check in. Tudor remembers your conversations and speaks from experience, not a textbook.',
    color: 'from-emerald-700 to-emerald-900',
    accent: 'bg-emerald-500',
  },
  {
    id: 'journal',
    icon: FileText,
    title: 'Daily Journal',
    subtitle: 'Write or Speak',
    description: 'Journal at least 5 times per week. Write a minimum of 10 words or use the microphone to record up to 5 minutes of voice which gets transcribed automatically. Your journal is private and only visible to you.',
    color: 'from-amber-700 to-amber-900',
    accent: 'bg-amber-500',
  },
  {
    id: 'mental-health',
    icon: Heart,
    title: 'Mental Health Check-in',
    subtitle: 'Daily Wellbeing Score',
    description: 'Rate your wellbeing from 1 to 10 each day. Add optional notes about how you are feeling. This helps track your Recovery journey and spots patterns over time.',
    color: 'from-rose-700 to-rose-900',
    accent: 'bg-rose-500',
  },
  {
    id: 'library',
    icon: BookOpen,
    title: 'Resources',
    subtitle: 'Readings, Lessons & Audio',
    description: 'Access your weekly readings and lessons in the Programme tab. Listen to Recovery audio from AA, NA, ACA and SMART Recovery. All readings support text-to-speech in 8 languages.',
    color: 'from-violet-700 to-violet-900',
    accent: 'bg-violet-500',
  },
  {
    id: 'meetings',
    icon: Users,
    title: 'Meeting Log',
    subtitle: '5 Meetings Per Week',
    description: 'Log your TSF meetings with before and after selfies for verification. Up to 2 Zoom meetings per week are allowed with screenshot proof. Location check-ins track your attendance.',
    color: 'from-cyan-700 to-cyan-900',
    accent: 'bg-cyan-500',
  },
  {
    id: 'voice',
    icon: Mic,
    title: 'Voice Input Everywhere',
    subtitle: 'Talk Instead of Type',
    description: 'You will see a microphone button on text fields throughout the app. Tap it to speak instead of type: in your journal, personal history, mental health notes, assessments and the bot chat.',
    color: 'from-teal-700 to-teal-900',
    accent: 'bg-teal-500',
  },
  {
    id: 'games',
    icon: Gamepad2,
    title: 'Recovery Games',
    subtitle: 'Healthy Distraction',
    description: 'Five custom games to help redirect cravings: Wordscape, Arrowscape, Memory Match, Zen Pattern and Quote Builder. A built-in timer gives you healthy breaks: 10 minutes on, 30 seconds off.',
    color: 'from-orange-700 to-orange-900',
    accent: 'bg-orange-500',
  },
  {
    id: 'emergency',
    icon: Phone,
    title: 'Emergency Help',
    subtitle: 'Always Available',
    description: 'The SOS button in the top bar gives you instant access to emergency numbers, crisis lines and substance helplines for your country. It auto-detects your location.',
    color: 'from-red-700 to-red-900',
    accent: 'bg-red-500',
  },
  {
    id: 'badges',
    icon: Award,
    title: 'Badges & Progress',
    subtitle: 'Celebrate Milestones',
    description: 'Earn badges for journal streaks, meeting milestones and week progression. Track your full journey from the Journey tab. Every step forward counts.',
    color: 'from-yellow-700 to-yellow-800',
    accent: 'bg-yellow-500',
  },
  {
    id: 'ready',
    icon: Shield,
    title: 'You are Ready',
    subtitle: 'One Day at a Time',
    description: 'Start with today\'s tasks on your dashboard. Open your journal, check in on your mental health and say hello to Tudor. Recovery is a journey: and you have just taken the first step.',
    color: 'from-blue-800 to-zinc-900',
    accent: 'bg-blue-500',
  },
];

function QuickStartGuide({ onComplete, autoPlay = false }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState('next');
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [slideProgress, setSlideProgress] = useState(0); // 0-100% of the current slide's timer
  const autoAdvanceRef = useRef(null);
  const progressRef = useRef(null);
  const voiceoverRef = useRef(null);
  const total = slides.length;
  const slide = slides[currentSlide];
  const isLast = currentSlide === total - 1;

  const goTo = useCallback((idx, dir) => {
    if (isAnimating || idx < 0 || idx >= total) return;
    setIsAnimating(true);
    setDirection(dir);
    setSlideProgress(0);
    setTimeout(() => {
      setCurrentSlide(idx);
      setIsAnimating(false);
    }, 200);
  }, [isAnimating, total]);

  const handleComplete = useCallback(async () => {
    try {
      await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/quickstart-complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
    } catch (e) {
      // Non-blocking — guide still closes
      console.debug('[QuickStart] complete request failed:', e?.message);
    }
    onComplete();
  }, [onComplete]);

  // Auto-advance timer for the 60-second tour mode.
  // Runs a 5s timer per slide, updates a smooth progress bar, and advances
  // (or completes on the last slide). Pausing halts both the timer and the
  // progress fill so the user can dwell on any slide.
  useEffect(() => {
    if (!autoPlay || isAnimating || isPaused) return;
    let elapsed = 0;
    const tickMs = 50;
    setSlideProgress(0);
    progressRef.current = setInterval(() => {
      elapsed += tickMs;
      const pct = Math.min(100, (elapsed / AUTOPLAY_SLIDE_MS) * 100);
      setSlideProgress(pct);
    }, tickMs);
    autoAdvanceRef.current = setTimeout(() => {
      if (isLast) {
        handleComplete();
      } else {
        goTo(currentSlide + 1, 'next');
      }
    }, AUTOPLAY_SLIDE_MS);
    return () => {
      if (autoAdvanceRef.current) clearTimeout(autoAdvanceRef.current);
      if (progressRef.current) clearInterval(progressRef.current);
    };
  }, [autoPlay, currentSlide, isAnimating, isPaused, isLast, goTo, handleComplete]);

  // Voiceover: plays per-slide narration if `voiceover_url` is defined on
  // the slide. Silent slides (current default) simply skip. Pausing pauses
  // the audio; unpausing resumes. When the slide changes, the previous
  // audio is stopped so slides never overlap.
  useEffect(() => {
    if (!autoPlay) return;
    const url = slide?.voiceover_url;
    // Stop any previous audio
    if (voiceoverRef.current) {
      try { voiceoverRef.current.pause(); } catch (e) { /* noop */ }
      voiceoverRef.current = null;
    }
    if (!url || isPaused) return;
    try {
      const audio = new Audio(url);
      audio.play().catch(() => { /* autoplay may be blocked; silent fail */ });
      voiceoverRef.current = audio;
    } catch (e) { /* noop */ }
    return () => {
      if (voiceoverRef.current) {
        try { voiceoverRef.current.pause(); } catch (err) { /* noop */ }
        voiceoverRef.current = null;
      }
    };
  }, [autoPlay, currentSlide, slide, isPaused]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        if (isLast) handleComplete();
        else goTo(currentSlide + 1, 'next');
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goTo(currentSlide - 1, 'prev');
      }
      if (e.key === 'Escape') handleComplete();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [currentSlide, isLast, goTo, handleComplete]);

  // Touch swipe
  const touchStart = React.useRef(null);
  const handleTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (!touchStart.current) return;
    const diff = touchStart.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        if (isLast) handleComplete();
        else goTo(currentSlide + 1, 'next');
      } else {
        goTo(currentSlide - 1, 'prev');
      }
    }
    touchStart.current = null;
  };

  const IconComponent = slide.icon;

  return (
    <div 
      className="fixed inset-0 z-[100] flex flex-col"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      data-testid="quickstart-guide"
    >
      {/* Background */}
      <div className={`absolute inset-0 bg-gradient-to-br ${slide.color} transition-all duration-500`} />

      {/* Persistent Axiom logo (only in auto-play tour mode; welcome slide
          still shows the big centred logo of its own, so we hide the
          persistent one there to avoid duplication). */}
      {autoPlay && !slide.useLogo && (
        <div className="absolute top-6 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center pointer-events-none">
          <div className="w-14 h-14 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20">
            <img src={AXIOM_LOGO} alt="Axiom" className="h-9" data-testid="tour-persistent-logo" />
          </div>
          <span className="mt-1 text-[10px] font-bold uppercase tracking-[0.2em] text-white/70">
            Recovery Programme
          </span>
        </div>
      )}

      {/* Overall tour progress bar (auto-play only): 60-second countdown
          across all 12 slides. Fills a segment per slide, with a smooth
          intra-slide fill so the timing is visible. */}
      {autoPlay && (
        <div
          className="absolute top-0 left-0 right-0 z-20 h-1 bg-white/10 overflow-hidden"
          data-testid="tour-progress-bar"
        >
          <div className="h-full flex">
            {slides.map((_, idx) => {
              let pct = 0;
              if (idx < currentSlide) pct = 100;
              else if (idx === currentSlide) pct = slideProgress;
              return (
                <div
                  key={idx}
                  className="flex-1 border-r border-black/10 last:border-r-0 relative"
                >
                  <div
                    className="absolute inset-y-0 left-0 bg-white"
                    style={{ width: `${pct}%`, transition: 'width 50ms linear' }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Skip button */}
      <button
        onClick={handleComplete}
        className="absolute top-4 right-4 z-30 text-white/60 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
        data-testid="quickstart-skip-btn"
        aria-label="Skip tour"
      >
        <X className="w-6 h-6" />
      </button>

      {/* Pause / Play toggle (auto-play mode only) */}
      {autoPlay && (
        <button
          onClick={() => setIsPaused((p) => !p)}
          className="absolute top-4 right-16 z-30 text-white/70 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
          data-testid="tour-pause-btn"
          aria-label={isPaused ? 'Resume tour' : 'Pause tour'}
        >
          {isPaused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
        </button>
      )}

      {/* Slide counter */}
      <div className="absolute top-5 left-4 z-20 text-white/50 text-xs font-mono">
        {currentSlide + 1} / {total}
      </div>

      {/* "60-second tour" badge (auto-play mode only) */}
      {autoPlay && (
        <div className="absolute bottom-4 left-4 z-20 text-white/40 text-[10px] font-bold uppercase tracking-[0.2em]" data-testid="tour-duration-label">
          60-second tour
        </div>
      )}

      {/* Content */}
      <div className={`relative flex-1 flex flex-col items-center justify-center px-6 max-w-lg mx-auto w-full ${autoPlay && !slide.useLogo ? 'pt-24' : ''}`}>
        <div className={`transition-all duration-200 ${isAnimating ? (direction === 'next' ? 'opacity-0 translate-x-8' : 'opacity-0 -translate-x-8') : 'opacity-100 translate-x-0'}`}>
          
          {/* Icon */}
          <div className="flex justify-center mb-8">
            {slide.useLogo ? (
              <div className="w-24 h-24 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20">
                <img src={AXIOM_LOGO} alt="Axiom" className="h-16" />
              </div>
            ) : (
              <div className={`w-20 h-20 rounded-2xl ${slide.accent} bg-opacity-30 backdrop-blur-sm flex items-center justify-center border border-white/20`}>
                <IconComponent className="w-10 h-10 text-white" />
              </div>
            )}
          </div>

          {/* Subtitle */}
          <p className="text-center text-white/60 text-xs font-bold uppercase tracking-[0.2em] mb-2">
            {slide.subtitle}
          </p>

          {/* Title */}
          <h1 className="text-center text-white text-3xl sm:text-4xl font-bold mb-6 leading-tight">
            {slide.title}
          </h1>

          {/* Description */}
          <p className="text-center text-white/80 text-base sm:text-lg leading-relaxed max-w-sm mx-auto">
            {slide.description}
          </p>
        </div>
      </div>

      {/* Bottom controls */}
      <div className="relative pb-10 px-6">
        {/* Progress dots */}
        <div className="flex justify-center gap-1.5 mb-8">
          {slides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => goTo(idx, idx > currentSlide ? 'next' : 'prev')}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                idx === currentSlide 
                  ? 'w-8 bg-white' 
                  : idx < currentSlide 
                    ? 'w-1.5 bg-white/50' 
                    : 'w-1.5 bg-white/20'
              }`}
              data-testid={`quickstart-dot-${idx}`}
            />
          ))}
        </div>

        {/* Navigation buttons */}
        <div className="flex items-center justify-between max-w-sm mx-auto">
          <button
            onClick={() => goTo(currentSlide - 1, 'prev')}
            disabled={currentSlide === 0}
            className={`flex items-center gap-1 px-4 py-2 rounded-full text-sm font-medium transition-all ${
              currentSlide === 0 
                ? 'opacity-0 pointer-events-none' 
                : 'text-white/70 hover:text-white hover:bg-white/10'
            }`}
            data-testid="quickstart-prev-btn"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>

          <button
            onClick={() => isLast ? handleComplete() : goTo(currentSlide + 1, 'next')}
            className={`flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold transition-all ${
              isLast 
                ? 'bg-white text-zinc-900 hover:bg-white/90 shadow-lg shadow-black/20' 
                : 'bg-white/20 text-white hover:bg-white/30 border border-white/30'
            }`}
            data-testid="quickstart-next-btn"
          >
            {isLast ? 'Get Started' : 'Next'}
            {!isLast && <ChevronRight className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}

export default QuickStartGuide;
